import { TableHeader } from '@/components/data-table/DataTable';
import { KeyInfoInterface } from '@/interfaces/KeyInfoInterface';
import { RestrictType } from '@/interfaces/RestrictType';
import { MasterDataStatusEnum, MasterDataStatusKeyInfo } from '@/items/MasterDataStatus';
import { OrderStatusEnum, OrderStatusKeyInfo } from '@/items/OrderStatus';
import { Model } from '@/models/Model';
import { formatDate } from '@/support/String';
import { Rules } from '@/support/ValidationRules';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';

// #region MODEL

export type PurchaseOrderIncludeType = 'note' | 'supplier' | 'brand';

export interface PurchaseOrderFilterInterface {
  brand?: string[];
  expected_at?: {
    to?: string;
    from?: string;
  }
  master_data_status?: MasterDataStatusEnum[],
  status?: OrderStatusEnum[],
  season?: string[],
  supplier?: string[];
};

export type PurchaseOrderFilterInterfaceKeyType = keyof PurchaseOrderFilterInterface;

export interface PurchaseOrderFilterFlatInterface {
  brand?: string[];
  expected_at_from?: string,
  expected_at_to?: string,
  master_data_status?: MasterDataStatusEnum[],
  status?: OrderStatusEnum[],
  season?: string[],
  supplier?: string[];
};

export type PurchaseOrderFilterFlatInterfaceKeyType = keyof PurchaseOrderFilterFlatInterface;

export class PurchaseOrder extends Model {
  protected $name = 'PurchaseOrder';

  protected $endpoint = '/purchase-orders';

  protected $fillable = [];

  protected $includes: PurchaseOrderIncludeType[] = [];

  protected filters: PurchaseOrderFilterInterface = {};

  id?: string;

  name?: string;

  supplier?: string | Supplier;

  brand?: string | Brand;

  file?: File;

  supplier_reference?: string;

  status?: OrderStatusEnum;

  master_data_status?: MasterDataStatusEnum;

  quantity?: string;

  note?: string;

  total_buy_price?: number;

  created_at?: string;

  expected_at?: string;

  completed_at?: string;

  confirmed_at?: string;

  last_delivery_at?: string;

  last_received_at?: string;

  submitted_at?: string;

  public include(includes: PurchaseOrderIncludeType | PurchaseOrderIncludeType[]): this {
    return super.include.call(this, includes);
  }

  public removeFilter(key: PurchaseOrderFilterInterfaceKeyType): this {
    return super.removeFilter.call(this, key);
  }

  public filter(filters: PurchaseOrderFilterInterfaceKeyType | PurchaseOrderFilterInterface, value?: any) {
    return super.filter.call(this, filters, value);
  }

  public importPurchaseOrder(id: string): this {
    this.$endpoint = `/purchase-orders/${id}/import`;
    return this;
  }

  public exportPurchaseOrder(purchaseOrderId: string): string {
    return `v1/purchase-orders/${purchaseOrderId}/export`;
  }
}

// #endregion

// #region MODEL: KEYS

export type PurchaseOrderKeys = Exclude<keyof PurchaseOrder,
  // Exclude keys:
  keyof Model
  | 'importPurchaseOrder'
  | 'exportPurchaseOrder'
>;

export type PurchaseOrderKeyInfoKeys = Exclude<PurchaseOrderKeys,
  // Exclude keys:
  'total_buy_price'
  | 'file'
>;

// #endregion

// #region MODEL: KEY INFO

export class PurchaseOrderKeyInfoClass implements RestrictType<PurchaseOrderKeyInfoKeys, KeyInfoInterface, PurchaseOrderKeyInfoClass> {
  get id(): KeyInfoInterface {
    return {
      label: '',
      labelShort: '',
      placeholder: '',
      rules: [],
    }
  }

  get name(): KeyInfoInterface {
    return {
      label: 'Order id',
      labelShort: 'Order Id',
      rules: [],
    }
  }

  get supplier(): KeyInfoInterface {
    return {
      label: 'Supplier',
      labelShort: 'Supplier',
      placeholder: 'Supplier',
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get brand(): KeyInfoInterface {
    return {
      label: 'Brand',
      labelShort: 'Brand',
      placeholder: 'Brand',
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get supplier_reference(): KeyInfoInterface {
    return {
      label: 'Supplier reference',
      labelShort: 'Supplier ref.',
      placeholder: 'Reference',
      rules: [],
    }
  }

  get status(): KeyInfoInterface {
    return {
      label: 'Order status',
      labelShort: 'Order status',
      placeholder: OrderStatusKeyInfo[OrderStatusEnum.DRAFT].label,
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get master_data_status(): KeyInfoInterface {
    return {
      label: 'Master data status',
      labelShort: 'Master data status',
      placeholder: MasterDataStatusKeyInfo[MasterDataStatusEnum.PENDING].label,
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get quantity(): KeyInfoInterface {
    return {
      label: 'Quantity ordered',
      labelShort: 'Qty ordered',
      placeholder: '5',
      rules: [],
    }
  }

  get note(): KeyInfoInterface {
    return {
      label: 'Note',
      labelShort: 'Note',
      placeholder: 'Something',
      rules: [],
    }
  }

  get created_at(): KeyInfoInterface {
    return {
      label: 'Created',
      labelShort: 'Created',
      rules: [],
    }
  }

  get completed_at(): KeyInfoInterface {
    return {
      label: 'Completed',
      labelShort: 'Completed',
      rules: [],
    }
  }

  get confirmed_at(): KeyInfoInterface {
    return {
      label: 'Confirmed',
      labelShort: 'Confirmed',
      rules: [],
    }
  }

  get expected_at(): KeyInfoInterface {
    return {
      label: 'Expected',
      labelShort: 'Expected',
      rules: [],
    }
  }

  get last_delivery_at(): KeyInfoInterface {
    return {
      label: 'Last delivery',
      labelShort: 'Last delivery',
      rules: [],
    }
  }

  get last_received_at(): KeyInfoInterface {
    return {
      label: 'Last received',
      labelShort: 'Last received',
      rules: [],
    }
  }

  get submitted_at(): KeyInfoInterface {
    return {
      label: 'Submitted',
      labelShort: 'Submitted',
      rules: [],
    }
  }
}

export const PurchaseOrderKeyInfo = new PurchaseOrderKeyInfoClass();

// #endregion

// #region MODEL: TABLE HEADER

export class PurchaseOrderTableHeaderClass implements RestrictType<PurchaseOrderKeyInfoKeys, TableHeader, PurchaseOrderTableHeaderClass> {
  get id(): TableHeader {
    return {
      value: 'id',
      text: PurchaseOrderKeyInfo.id.labelShort,
      class: '',
    }
  }

  get name(): TableHeader {
    return {
      value: 'name',
      text: PurchaseOrderKeyInfo.name.labelShort,
      class: '',
      action: '/purchase-orders/{id}',
      sortable: {
        key: 'name',
        order: 'ASC'
      },
    }
  }

  get supplier(): TableHeader {
    return {
      value: 'supplier',
      text: PurchaseOrderKeyInfo.supplier.labelShort,
      sortable: {
        key: 'supplier',
        order: 'ASC'
      },
      transform: (supplier: Supplier) => supplier && supplier.name ? supplier.name : '-',
    }
  }

  get brand(): TableHeader {
    return {
      value: 'brand',
      text: PurchaseOrderKeyInfo.brand.labelShort,
      sortable: {
        key: 'brand',
        order: 'ASC'
      },
      transform: (brand: Brand) => brand && brand.name ? brand.name : '-',
    }
  }

  get supplier_reference(): TableHeader {
    return {
      value: 'supplier_reference',
      text: PurchaseOrderKeyInfo.supplier_reference.labelShort,
      class: '',
      sortable: {
        key: 'supplier_reference',
        order: 'ASC'
      },
    }
  }

  get status(): TableHeader {
    return {
      value: 'status',
      text: PurchaseOrderKeyInfo.status.labelShort,
      sortable: {
        key: 'status',
        order: 'ASC'
      },
      transform: (status: undefined | OrderStatusEnum) => {
        if (status === undefined) { status = OrderStatusEnum.PENDING; }

        const orderStatusLabelShort = OrderStatusKeyInfo[status]
          && OrderStatusKeyInfo[status].labelShort
            ? OrderStatusKeyInfo[status].labelShort
            : status;

        return `<div class='statusPill statusPill--${status} text-center font-semibold text-base'>${orderStatusLabelShort}</div>`;
      },
    }
  }

  get master_data_status(): TableHeader {
    return {
      value: 'master_data_status',
      text: PurchaseOrderKeyInfo.master_data_status.labelShort,
      sortable: {
        key: 'master_data_status',
        order: 'ASC'
      },
      transform: (master_data_status: undefined | MasterDataStatusEnum) => {
        if (master_data_status === undefined) { master_data_status = MasterDataStatusEnum.PENDING; }

        const masterDataStatusLabelShort = MasterDataStatusKeyInfo[master_data_status]
          && MasterDataStatusKeyInfo[master_data_status].labelShort
            ? MasterDataStatusKeyInfo[master_data_status].labelShort
            : master_data_status;

        return `${masterDataStatusLabelShort}`;
      },
    }
  }

  get quantity(): TableHeader {
    return {
      value: 'quantity',
      text: PurchaseOrderKeyInfo.quantity.labelShort,
      class: '',
      sortable: {
        key: 'quantity',
        order: 'ASC'
      },
    }
  }

  get note(): TableHeader {
    return {
      value: 'note',
      text: PurchaseOrderKeyInfo.note.labelShort,
    }
  }

  get created_at(): TableHeader {
    return {
      value: 'created_at',
      text: PurchaseOrderKeyInfo.created_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'created_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get confirmed_at(): TableHeader {
    return {
      value: 'confirmed_at',
      text: PurchaseOrderKeyInfo.confirmed_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'confirmed_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get completed_at(): TableHeader {
    return {
      value: 'completed_at',
      text: PurchaseOrderKeyInfo.completed_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'completed_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get expected_at(): TableHeader {
    return {
      value: 'expected_at',
      text: PurchaseOrderKeyInfo.expected_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'expected_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get last_delivery_at(): TableHeader {
    return {
      value: 'last_delivery_at',
      text: PurchaseOrderKeyInfo.last_delivery_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'last_delivery_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get last_received_at(): TableHeader {
    return {
      value: 'last_received_at',
      text: PurchaseOrderKeyInfo.last_received_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'last_received_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }

  get submitted_at(): TableHeader {
    return {
      value: 'submitted_at',
      text: PurchaseOrderKeyInfo.submitted_at.labelShort,
      class: 'white-space-no-wrap',
      sortable: {
        key: 'submitted_at',
        order: 'ASC'
      },
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }
}

export const PurchaseOrderTableHeader = new PurchaseOrderTableHeaderClass();

// #endregion
